import { useContext, useEffect, useRef, useState } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@easyflow/ui-frontend/components/ui/dialog';
import { Button } from '@easyflow/ui-frontend/components/ui/button';

/** BrowserRouter navigation guard. No authorization changes and no global draft storage. */
export function usePendingNavigation(dirty: boolean) {
  const context = useContext(UNSAFE_NavigationContext);
  const pending = useRef<(() => void) | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!dirty) return;
    const beforeUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', beforeUnload);
    const readIndex = () => {
      const state: unknown = window.history.state;
      return state && typeof state === 'object' && 'idx' in state && typeof state.idx === 'number' ? state.idx : null;
    };
    const initialIndex = readIndex();
    let restoring = false;
    let proceeding = false;
    const onPopState = (event: PopStateEvent) => {
      if (proceeding) return;
      const index = readIndex();
      if (initialIndex === null || index === null) return;
      event.stopImmediatePropagation();
      if (restoring) { restoring = false; setOpen(true); return; }
      const delta = index - initialIndex;
      if (!delta) return;
      restoring = true;
      pending.current = () => { proceeding = true; window.history.go(delta); };
      window.history.go(-delta);
    };
    window.addEventListener('popstate', onPopState, true);
    const navigator = context?.navigator;
    const originalPush = navigator?.push;
    const originalReplace = navigator?.replace;
    const originalGo = navigator?.go;
    if (navigator && originalPush && originalReplace && originalGo) {
      navigator.push = (...args) => { pending.current = () => originalPush.apply(navigator, args); setOpen(true); };
      navigator.replace = (...args) => { pending.current = () => originalReplace.apply(navigator, args); setOpen(true); };
      navigator.go = (...args) => { pending.current = () => originalGo.apply(navigator, args); setOpen(true); };
    }
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      window.removeEventListener('popstate', onPopState, true);
      if (navigator && originalPush && originalReplace && originalGo) {
        navigator.push = originalPush; navigator.replace = originalReplace; navigator.go = originalGo;
      }
    };
  }, [context, dirty]);
  return <Dialog open={open} onOpenChange={value => { setOpen(value); if (!value) pending.current = null; }}>
    <DialogContent><DialogHeader><DialogTitle>Modifiche non salvate</DialogTitle>
      <DialogDescription>Uscendo da questa pagina perderai la bozza. Puoi restare e salvarla prima di proseguire.</DialogDescription></DialogHeader>
      <DialogFooter><Button variant="outline" onClick={() => { pending.current = null; setOpen(false); }}>Continua a modificare</Button>
        <Button onClick={() => { const action = pending.current; pending.current = null; setOpen(false); action?.(); }}>Scarta e prosegui</Button></DialogFooter>
    </DialogContent>
  </Dialog>;
}
